import { BaseApiClient } from "../api/baseApiClient.service";
import { AnalyticsService } from "../analytics/analytics.service";

/**
 * Represents a time segment that has been listened to
 */
export interface ListenedSegment {
	start: number; // Start time in seconds
	end: number; // End time in seconds
}

/**
 * Represents the playback progress for a podcast episode
 */
export interface PodcastPlaybackProgress {
	id: string;
	userId: string;
	podcastId: string;
	currentTime: number; // Current playback position in seconds
	duration?: number; // Total duration of the podcast in seconds
	completed: boolean; // Whether the user has completed listening to this podcast
	lastListenedAt: string; // ISO timestamp of when this was last updated
	createdAt: string; // ISO timestamp of when this record was created
	updatedAt: string; // ISO timestamp of when this record was last updated
	listenedSegments?: ListenedSegment[]; // Segments that have been actually listened to
}

/**
 * Request payload for updating playback progress
 */
export interface UpdateProgressRequest {
	currentTime: number; // Current playback position in seconds (required)
	duration?: number; // Total duration of the podcast in seconds (optional)
	completed?: boolean; // Whether the user has completed listening (optional)
	listenedSegments?: ListenedSegment[]; // Segments that have been listened to (optional)
}

/**
 * Service for managing podcast playback progress.
 * Tracks where users are in their podcast listening experience and maintains listening history.
 */
export class PodcastPlaybackProgressService {
	static path = "podcast-playback-progress";

	/**
	 * Updates the playback progress for a specific podcast.
	 * This should be called periodically while the user is listening to track their position.
	 *
	 * @param podcastId - The ID of the podcast being played
	 * @param progress - The progress data to update
	 * @returns The updated progress record
	 *
	 * @example
	 * ```typescript
	 * // Update progress every 10 seconds while playing
	 * await PodcastPlaybackProgressService.updateProgress('podcast-123', {
	 *   currentTime: 245.5,
	 *   duration: 1800,
	 *   completed: false
	 * });
	 * ```
	 */
	/** Milestones (in %) that have already been reported for each podcast this session */
	private static reportedMilestones = new Map<string, Set<number>>();

	/** The milestone thresholds we care about */
	private static readonly MILESTONES = [20, 90] as const;

	static async updateProgress(
		podcastId: string,
		progress: UpdateProgressRequest,
	): Promise<PodcastPlaybackProgress> {
		const result = await BaseApiClient.getInstance().put<
			PodcastPlaybackProgress,
			UpdateProgressRequest
		>(`${PodcastPlaybackProgressService.path}/${podcastId}/progress`, progress);

		// Only fire analytics at meaningful milestones (25 / 50 / 75 / 100%)
		// to avoid cluttering PostHog with a tick every 15 seconds.
		const duration = progress.duration ?? 0;
		if (duration > 0) {
			const pct = (progress.currentTime / duration) * 100;

			if (!PodcastPlaybackProgressService.reportedMilestones.has(podcastId)) {
				PodcastPlaybackProgressService.reportedMilestones.set(
					podcastId,
					new Set(),
				);
			}
			const reported =
				PodcastPlaybackProgressService.reportedMilestones.get(podcastId)!;

			for (const milestone of PodcastPlaybackProgressService.MILESTONES) {
				if (pct >= milestone && !reported.has(milestone)) {
					reported.add(milestone);
					AnalyticsService.track("listening_milestone", {
						podcast_id: podcastId,
						milestone,
						current_time: Math.round(progress.currentTime),
						duration: Math.round(duration),
						listened_seconds: progress.listenedSegments
							? Math.round(
									PodcastPlaybackProgressService.calculateListenedDuration(
										progress.listenedSegments,
									),
								)
							: undefined,
					});

					// Habit Loop v1 - broadcast the milestone so UI can offer a
					// next-step prompt when the user nears the end of a podcast.
					if (typeof window !== "undefined") {
						window.dispatchEvent(
							new CustomEvent("podhive:listening-milestone", {
								detail: { podcastId, milestone },
							}),
						);
					}
				}
			}
		}

		return result;
	}

	/**
	 * Gets the current playback progress for a specific podcast.
	 * Returns null if no progress has been recorded for this podcast.
	 *
	 * @param podcastId - The ID of the podcast to get progress for
	 * @returns The progress record, or throws 404 error if not found
	 *
	 * @example
	 * ```typescript
	 * try {
	 *   const progress = await PodcastPlaybackProgressService.getProgress('podcast-123');
	 *   console.log(`Resume at ${progress.currentTime} seconds`);
	 * } catch (error) {
	 *   // Handle 404 - no progress recorded yet
	 * }
	 * ```
	 */
	static async getProgress(
		podcastId: string,
	): Promise<PodcastPlaybackProgress> {
		return BaseApiClient.getInstance().get<PodcastPlaybackProgress>(
			`${PodcastPlaybackProgressService.path}/${podcastId}/progress`,
		);
	}

	/**
	 * Gets the user's recent listening history.
	 * Returns podcasts ordered by most recently listened to first.
	 *
	 * @param limit - Maximum number of records to return (default: 20, max: 100)
	 * @returns Array of recent progress records ordered by most recent
	 *
	 * @example
	 * ```typescript
	 * // Get the 10 most recently listened podcasts
	 * const recentListens = await PodcastPlaybackProgressService.getRecentListens(10);
	 * recentListens.forEach(progress => {
	 *   console.log(`Podcast ${progress.podcastId}: ${progress.currentTime}s / ${progress.duration}s`);
	 * });
	 * ```
	 */
	static async getRecentListens(
		limit = 20,
	): Promise<PodcastPlaybackProgress[]> {
		// Validate limit range
		const validatedLimit = Math.min(Math.max(limit, 1), 100);

		return BaseApiClient.getInstance().get<PodcastPlaybackProgress[]>(
			`${PodcastPlaybackProgressService.path}/recent`,
			{ limit: validatedLimit },
		);
	}

	/**
	 * Helper method to determine if a podcast should be considered "completed"
	 * based on current time and duration.
	 *
	 * A podcast is considered completed if the user has listened to at least 95% of it.
	 *
	 * @param currentTime - Current playback position in seconds
	 * @param duration - Total duration in seconds
	 * @returns true if the podcast should be marked as completed
	 *
	 * @example
	 * ```typescript
	 * const isComplete = PodcastPlaybackProgressService.shouldMarkAsCompleted(1710, 1800);
	 * // Returns true (95% of 1800 seconds)
	 * ```
	 */
	static shouldMarkAsCompleted(currentTime: number, duration: number): boolean {
		if (duration <= 0) return false;
		const percentComplete = (currentTime / duration) * 100;
		return percentComplete >= 95;
	}

	/**
	 * Helper method to calculate the percentage of podcast completed.
	 *
	 * @param currentTime - Current playback position in seconds
	 * @param duration - Total duration in seconds
	 * @returns Percentage completed (0-100)
	 *
	 * @example
	 * ```typescript
	 * const percent = PodcastPlaybackProgressService.getProgressPercentage(900, 1800);
	 * // Returns 50
	 * ```
	 */
	static getProgressPercentage(currentTime: number, duration: number): number {
		if (duration <= 0) return 0;
		return Math.min((currentTime / duration) * 100, 100);
	}

	/**
	 * Calculate total listened duration from segments
	 *
	 * @param segments - Array of listened segments
	 * @returns Total listened duration in seconds
	 */
	static calculateListenedDuration(segments: ListenedSegment[]): number {
		if (!segments || segments.length === 0) {
			return 0;
		}

		return segments.reduce((total, segment) => {
			return total + (segment.end - segment.start);
		}, 0);
	}

	/**
	 * Check if a specific time has been listened to
	 *
	 * @param segments - Array of listened segments
	 * @param time - Time in seconds to check
	 * @returns True if the time falls within any listened segment
	 */
	static hasListenedToTime(segments: ListenedSegment[], time: number): boolean {
		if (!segments || segments.length === 0) return false;

		return segments.some(
			(segment) => time >= segment.start && time <= segment.end,
		);
	}

	/**
	 * Merge overlapping segments (client-side utility)
	 *
	 * @param segments - Array of segments to merge
	 * @returns Merged segments
	 */
	static mergeSegments(segments: ListenedSegment[]): ListenedSegment[] {
		if (!segments || segments.length === 0) return [];

		// Sort by start time
		const sorted = [...segments].sort((a, b) => a.start - b.start);

		// Merge overlapping segments
		const merged: ListenedSegment[] = [];
		for (const segment of sorted) {
			if (merged.length === 0) {
				merged.push(segment);
				continue;
			}

			const last = merged[merged.length - 1];
			// If segments overlap or are adjacent (within 2 seconds), merge them
			if (segment.start <= last.end + 2) {
				last.end = Math.max(last.end, segment.end);
			} else {
				merged.push(segment);
			}
		}

		return merged;
	}
}
